import { startCase } from 'es-toolkit';
import React from 'react';
import { connect, ResolveThunks } from 'react-redux';
import { Button, SxProps, Theme } from '@mui/material';

import { User } from '@paragon/contract-library';

import { startEditingJob } from '../actions/jobActions';
import { lockedColumns, PropertyKey } from '../propertyMetadata';
import { getActiveView } from '../selectors/getActiveView';
import { getAllResults } from '../selectors/getAllResults';
import { RootState } from '../store';
import { setPageIndex, sortByColumn } from '../store/gridSlice';
import { JobWithUsers, onToggleJob } from '../store/jobsSlice';
import DataGrid, { ColumnDefinition, ValueFormatterParametersStrict } from './tools/DataGrid';

const formatDate = (date: Date) => `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;

const formatTime = (date: Date) => {
  const hours12 = date.getHours() % 12 || 12;
  const amPm = date.getHours() >= 12 ? 'PM' : 'AM';
  return `${hours12}:${`${date.getMinutes()}`.padStart(2, '0')} ${amPm}`;
};

const parseDate = (source: string) => {
  return !source.includes('Z') ? new Date(`${source}Z`) : new Date(source);
};

const dateValueFormatter = (params: ValueFormatterParametersStrict<JobWithUsers, string>) => {
  const date = parseDate(params.value);
  return `${formatDate(date)} ${formatTime(date)}`;
};

const regionValueFormatter = (params: ValueFormatterParametersStrict<JobWithUsers, readonly string[]>) =>
  params.value.join(', ');

const listOfUsersValueFormatter = (
  params: ValueFormatterParametersStrict<JobWithUsers, readonly User[] | undefined>,
) => {
  if (!params.value) return '';
  return params.value.map((user) => `${user.firstName} ${user.lastName}`).join(', ');
};

const listOfOrganizationsValueFormatter = (params: ValueFormatterParametersStrict<JobWithUsers, readonly string[]>) =>
  params.value.join(', ');

const userValueFormatter = (params: ValueFormatterParametersStrict<JobWithUsers, User | undefined>) => {
  if (!params.value) return '';
  return `${params.value.firstName} ${params.value.lastName}`;
};

const enumValueFormatter = (params: ValueFormatterParametersStrict<JobWithUsers, string>) => startCase(params.value);

const idColumnDefinition: ColumnDefinition<JobWithUsers, 'id'> = {
  field: 'id',
  headerName: 'ID',
};

const regionsColumnDefinition: ColumnDefinition<JobWithUsers, 'regions'> = {
  field: 'regions',
  headerName: 'Regions',
  valueFormatter: regionValueFormatter,
};

const jobTypeColumnDefinition: ColumnDefinition<JobWithUsers, 'jobType'> = {
  field: 'jobType',
  headerName: 'Job Type',
  valueFormatter: enumValueFormatter,
};

const creatorColumnDefinition: ColumnDefinition<JobWithUsers, 'creator'> = {
  field: 'creator',
  headerName: 'Created By',
  valueFormatter: userValueFormatter,
};

const assignedToColumnDefinition: ColumnDefinition<JobWithUsers, 'assignedTo'> = {
  field: 'assignedTo',
  headerName: 'Assigned To',
  valueFormatter: listOfUsersValueFormatter,
};

const createdTimestampColumnDefinition: ColumnDefinition<JobWithUsers, 'createdTimestamp'> = {
  field: 'createdTimestamp',
  headerName: 'Created At',
  valueFormatter: dateValueFormatter,
};

const numberOfDesignsColumnDefinition: ColumnDefinition<JobWithUsers, 'numberOfDesigns'> = {
  field: 'numberOfDesigns',
  headerName: '# Designs',
};

const openedByColumnDefinition: ColumnDefinition<JobWithUsers, 'openedBy'> = {
  field: 'openedBy',
  headerName: 'Opened By',
  valueFormatter: listOfUsersValueFormatter,
};

const organizationOwnedByColumnDefinition: ColumnDefinition<JobWithUsers, 'organizationOwnedBy'> = {
  field: 'organizationOwnedBy',
  headerName: 'Customer',
};

const jobStatusColumnDefinition: ColumnDefinition<JobWithUsers, 'jobStatus'> = {
  field: 'jobStatus',
  headerName: 'Status',
  valueFormatter: enumValueFormatter,
};

const jobPriorityColumnDefinition: ColumnDefinition<JobWithUsers, 'jobPriority'> = {
  field: 'jobPriority',
  headerName: 'Priority',
};

const sharedWithOrganizationsColumnDefinition: ColumnDefinition<JobWithUsers, 'sharedWithOrganizations'> = {
  field: 'sharedWithOrganizations',
  headerName: 'Shared With',
  valueFormatter: listOfOrganizationsValueFormatter,
};

const billingLocationColumnDefinition: ColumnDefinition<JobWithUsers, 'billingLocation'> = {
  field: 'billingLocation',
  headerName: 'Location',
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ResolveThunks<typeof actionCreators>;
interface OwnProps {
  scrollToTop: () => void;
}
type Props = StateProps & DispatchProps & OwnProps;

function ProjectGrid(props: Props) {
  const MarkupLinkRenderCell = (params: ValueFormatterParametersStrict<JobWithUsers, string>) => (
    <Button
      sx={{
        color: '#2d2d2d',
        ':hover': {
          color: '#eeeeee',
        },
      }}
      fullWidth
      variant="contained"
      onClick={() => {
        props.startEditingJob(params.row);
        props.scrollToTop();
      }}
    >
      {params.value}
    </Button>
  );

  const nameColumnDefinition: ColumnDefinition<JobWithUsers, 'name'> = {
    field: 'name',
    headerName: 'Name',
    renderCell: MarkupLinkRenderCell,
  };

  const columnDefinitions: readonly ColumnDefinition<JobWithUsers, PropertyKey>[] = [
    idColumnDefinition,
    nameColumnDefinition,
    regionsColumnDefinition,
    jobTypeColumnDefinition,
    creatorColumnDefinition,
    assignedToColumnDefinition,
    createdTimestampColumnDefinition,
    numberOfDesignsColumnDefinition,
    openedByColumnDefinition,
    organizationOwnedByColumnDefinition,
    jobStatusColumnDefinition,
    jobPriorityColumnDefinition,
    sharedWithOrganizationsColumnDefinition,
    billingLocationColumnDefinition,
  ];

  const visibleColumns = lockedColumns.concat(props.visibleColumns);
  const visibleColumnDefinitions = columnDefinitions.filter((column) => visibleColumns.includes(column.field));

  const getRowSx = (rowData: JobWithUsers): SxProps<Theme> => {
    switch (rowData.jobType) {
      case 'Seal':
        return {
          backgroundColor: 'background.default',
          '& td': {
            '& button': {
              bgcolor: 'lightblue',
            },
          },
        };
      case 'Repair':
        return {
          backgroundColor: 'background.default',
          '& td': {
            '& button': {
              bgcolor: 'lightcoral',
            },
          },
        };
      case 'SpecialEngineering':
        return {
          backgroundColor: 'background.default',
          '& td': {
            '& button': {
              bgcolor: '#eee',
            },
          },
        };
      default:
        return {};
    }
  };

  return (
    <DataGrid
      rows={props.results}
      columns={visibleColumnDefinitions}
      onRequestSort={props.sortByColumn}
      sortColumn={props.sortColumn}
      isSortAscending={props.isSortAscending}
      setPageIndex={props.setPageIndex}
      pageIndex={props.index}
      pageSize={20}
      isRowSelected={(row) => props.selectedJobIds.includes(row.id)}
      onSelectRow={(row) => {
        props.onToggleJob(row.id);
      }}
      getRowSx={getRowSx}
    />
  );
}

const mapStateToProps = (state: RootState) => ({
  index: state.grid.index,
  isSortAscending: state.grid.isAscending,
  results: getAllResults(state),
  selectedJobIds: state.jobs.selectedJobIds,
  sortColumn: state.grid.sort,
  visibleColumns: getActiveView(state).visibleColumns,
});

const actionCreators = {
  onToggleJob,
  sortByColumn,
  setPageIndex,
  startEditingJob,
};

export default connect(mapStateToProps, actionCreators)(ProjectGrid);
